import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';

const isNoReturnOrSaleReference = (reference?: SHIPPING_ITEM_REFERENCE) =>
  reference === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION ||
  reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO;

/**
 * Indica si algún elemento enviado por Acronex tiene una distribución de
 * cantidades incompleta (estoque técnico + descartada + a devolver no suma
 * la cantidad enviada), salvo en referencias sin devolución/venta de repuesto.
 * Misma regla que valida ServiceOrderShippedItems al editar los elementos.
 */
export const hasInvalidServiceOrderItemsDistribution = (
  serviceOrderItems: ServiceOrderItem[],
): boolean =>
  serviceOrderItems.some(item => {
    if (!item.package_item || typeof item.package_item === 'number') {
      return false;
    }

    if (isNoReturnOrSaleReference(item.package_item.item_shipped_reference)) {
      return false;
    }

    const total =
      (item.stock_technician_amount ?? 0) +
      (item.discarded_amount ?? 0) +
      (item.to_return_amount ?? 0);

    return total !== (item.package_item.execute_amount ?? 0);
  });

/**
 * Formatea un valor numérico o string con un sufijo. Retorna '-' si el valor es vacío/null/undefined.
 */
export const formatWithSuffix = (
  value: number | string | null | undefined,
  suffix: string,
): string => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  return `${value} ${suffix}`;
};

/**
 * Formatea un booleano como 'Sí'/'No'. Retorna '-' si el valor es null/undefined.
 */
export const formatBoolean = (value?: boolean | null, yesLabel = 'Sí', noLabel = 'No'): string =>
  value === undefined || value === null ? '-' : value ? yesLabel : noLabel;

/**
 * Extrae el nombre de archivo de una URL (descartando query params), para mostrar
 * en links de descarga sin la query string de S3/CDN.
 */
export const getFileNameFromUrl = (url: string, fallback = 'archivo.pdf'): string =>
  decodeURIComponent(url.split('?')[0].split('/').pop() ?? fallback);

/**
 * Campos de facturación obligatorios antes de poder enviar la orden a facturación
 * (misma obligatoriedad que tenían los Form.Item de ServiceOrderBillingData.tsx).
 * Devuelve las etiquetas (sin traducir) de los campos faltantes.
 */
export const getMissingBillingFields = (
  serviceOrder: {
    currency?: unknown;
    hourly_rate?: number | null;
    kilometres_rate?: number | null;
    hours_estimated?: number | null;
    kilometres_estimated?: number | null;
    technician_pay?: boolean | null;
    customer_invoice?: boolean | null;
    hours_covered?: number | null;
    kilometres_covered?: number | null;
    payment_method?: unknown;
    payment_method_client?: unknown;
  },
  isBrasilSelected: boolean,
): string[] => {
  const missing: string[] = [];

  if (serviceOrder.currency == undefined) missing.push('Tipo de moneda');
  if (serviceOrder.hourly_rate == undefined) missing.push('Precio por hora');
  if (serviceOrder.kilometres_rate == undefined) missing.push('Precio por kilómetro');
  if (serviceOrder.hours_estimated == undefined) missing.push('Horas');
  if (serviceOrder.kilometres_estimated == undefined) missing.push('Kilómetros recorridos');
  if (serviceOrder.technician_pay == undefined) missing.push('Pagar servicio al técnico');
  if (serviceOrder.customer_invoice == undefined) missing.push('Facturar al cliente');
  if (serviceOrder.hours_covered == undefined) missing.push('Horas cubiertas por Acronex');
  if (serviceOrder.kilometres_covered == undefined)
    missing.push('Kilómetros cubiertos por Acronex');

  if (isBrasilSelected && serviceOrder.technician_pay && serviceOrder.payment_method == undefined) {
    missing.push('Método de pago al técnico');
  }

  if (
    isBrasilSelected &&
    serviceOrder.customer_invoice &&
    serviceOrder.payment_method_client == undefined
  ) {
    missing.push('Método de cobro al cliente');
  }

  return missing;
};
